/**
 * Clicking the active theme again (Gordon 2026-11-01): the theme's OWN three
 * colours swap roles — no new colours. Pure, so it's tested across every theme
 * (theme-rotation.test.ts).
 */

import type { ColorTokens } from '@/types/theme';

export function adjustLightness(hsl: string, delta: number): string {
  const p = hsl.trim().split(/\s+/);
  if (p.length < 3) return hsl;
  const l = Math.max(2, Math.min(97, parseFloat(p[2]) + delta));
  return `${p[0]} ${p[1]} ${l.toFixed(1)}%`;
}

function contrastFg(hsl: string): string {
  const p = hsl.trim().split(/\s+/);
  const l = p.length >= 3 ? parseFloat(p[2]) : 50;
  return l > 45 ? '0 0% 5%' : '0 0% 95%';
}

/** WCAG contrast between two "H S% L%" colours (for picking readable text). */
function hslLuminance(hsl: string): number {
  const p = hsl.trim().split(/\s+/).map(v => parseFloat(v));
  const h = (p[0] || 0) / 360, sat = (p[1] || 0) / 100, l = (p[2] || 0) / 100;
  const q = l < 0.5 ? l * (1 + sat) : l + sat - l * sat, pp = 2 * l - q;
  const ch = (t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    const v = t < 1 / 6 ? pp + (q - pp) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? pp + (q - pp) * (2 / 3 - t) * 6 : pp;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = sat === 0 ? [l, l, l].map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)) : [ch(h + 1 / 3), ch(h), ch(h - 1 / 3)];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function hslContrast(a: string, b: string): number {
  const x = hslLuminance(a), y = hslLuminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/**
 * Clicking the active theme again (Gordon 2026-11-01): its OWN three colours —
 * background (A), accent/buttons (B) and text (C) — swap roles; no new colours.
 *   step 0 : bg A · accent B · text C   (the theme as designed)
 *   step 1 : bg B · accent A · text C   (accent becomes the background)
 *   step 2 : bg C · accent A · text B   (e.g. white background, purple text, dark accent)
 * The 4th click is step 0 again. Text that would be hard to read on the new
 * background falls back to whichever of the three colours reads best.
 */
export function buildRoleRotation(colors: ColorTokens | undefined | null, step: number): ColorTokens {
  if (!colors || step === 0) return (colors ?? {}) as ColorTokens;
  const A = colors.background, B = colors.primary, C = colors.foreground;
  const [bg, accent, text0] = step === 1 ? [B, A, C] : [C, A, B];
  const trio = [A, B, C];
  // Readable text: the preferred colour if it reaches 4.5:1, else the best of the
  // theme's own three, else near-black/white (only when none of the three reads).
  const best = (on: string, prefer: string) => {
    if (hslContrast(prefer, on) >= 4.5) return prefer;
    const own = trio.filter(c => c !== on).sort((x, y) => hslContrast(y, on) - hslContrast(x, on))[0];
    return own && hslContrast(own, on) >= 4.5 ? own : contrastFg(on);
  };
  const text = best(bg, text0);
  const accentText = best(accent, bg);

  const bgL = parseFloat(bg.trim().split(/\s+/)[2] ?? '50');
  const card = adjustLightness(bg, bgL < 50 ? +5 : -6);
  const muted = adjustLightness(bg, bgL < 50 ? +3 : -8);
  const border = adjustLightness(bg, bgL < 50 ? +12 : -18);
  const sidebar = adjustLightness(bg, bgL < 50 ? -3 : -5);
  return {
    ...colors,
    background: bg, foreground: text,
    card, cardForeground: text, popover: card, popoverForeground: text,
    headerBackground: bg,
    primary: accent, primaryForeground: accentText,
    accent, accentForeground: accentText,
    secondary: muted, secondaryForeground: text,
    muted, mutedForeground: text,
    border, input: border, ring: accent,
    playerToggleBackground: accent,
    sidebarBackground: sidebar, sidebarForeground: text,
    sidebarPrimary: accent, sidebarPrimaryForeground: accentText,
    sidebarAccent: accent, sidebarAccentForeground: accentText,
    sidebarBorder: border, sidebarRing: accent,
  };
}

